"use strict";
import { InterfaceEventName } from "@uniswap/analytics-events";
import QueryTokenLogo from "components/Logo/QueryTokenLogo";
import { DeltaArrow, DeltaText } from "components/Tokens/TokenDetails/Delta";
import { LoadingBubble } from "components/Tokens/loading";
import Column from "components/deprecated/Column";
import { NATIVE_CHAIN_ID } from "constants/tokens";
import { gqlTokenToCurrencyInfo } from "graphql/data/types";
import { getTokenDetailsURL } from "graphql/data/util";
import styled, { css } from "lib/styled-components";
import { searchTokenToTokenSearchResult } from "lib/utils/searchBar";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { ThemedText } from "theme/components";
import { EllipsisStyle } from "theme/components/styles";
import { Flex } from "ui/src";
import { GoldenBadgeVerified } from "components/BadgeProvider";
import WarningIcon from "components/warnings/WarningIcon";
import { getWarningIconColors } from "components/warnings/utils";
import { TokenStandard } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { fromGraphQLChain } from "features/chains/utils";
import { addToSearchHistory } from "features/search/searchHistorySlice";
import { sendAnalyticsEvent } from "features/telemetry/send";
import { getTokenWarningSeverity } from "features/tokens/safetyUtils";
import { shortenAddress } from "utilities/src/addresses";
import { NumberType, useFormatter } from "utils/formatNumbers";
const PriceChangeContainer = styled.div`
  display: flex;
  align-items: center;
  padding-top: 4px;
  gap: 2px;
`;
const SuggestionRowStyles = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  padding: 8px 16px;
  transition: background ${({ theme }) => theme.transition.duration.medium}
    ${({ theme }) => theme.transition.timing.ease};
  :hover {
    background: ${({ theme }) => theme.surface3};
  }
  ${({ $isFocused, theme }) => $isFocused && `
  background: ${theme.surface2};
`}
`;
const StyledLink = styled(Link)`
  ${SuggestionRowStyles}
`;
const SkeletonSuggestionRow = styled.div`
  ${SuggestionRowStyles}
`;
const CollectionImageStyles = css`
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  background: ${({ theme }) => theme.surface3};
  flex-shrink: 0;
`;
const CollectionImage = styled.img`
  ${CollectionImageStyles}
`;
const BrokenCollectionImage = styled.div`
  ${CollectionImageStyles}
`;
const PrimaryText = styled(ThemedText.SubHeader)`
  ${EllipsisStyle}
`;
const SecondaryContainer = styled(Column)`
  text-align: right;
  align-items: flex-end;
`;
function asSearchTokenSuggestion(token) {
  return token;
}
export function suggestionIsToken(suggestion) {
  return suggestion.decimals !== void 0;
}
export function SuggestionRow({
  suggestion,
  isHovered,
  setHoveredIndex,
  toggleOpen,
  index,
  eventProperties
}) {
  const { t } = useTranslation();
  const isToken = suggestionIsToken(suggestion);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formatFiatPrice, formatDelta, formatNumberOrString } = useFormatter();
  const [brokenCollectionImage, setBrokenCollectionImage] = useState(false);
  const tokenWarningSeverity = isToken ? getTokenWarningSeverity(gqlTokenToCurrencyInfo(suggestion)) : void 0;
  const { colorSecondary: warningIconColor } = getWarningIconColors(tokenWarningSeverity);
  const handleClick = useCallback(() => {
    const tokenStandard = suggestionIsToken(suggestion) ? asSearchTokenSuggestion(suggestion).standard : void 0;
    const address = !suggestion.address && tokenStandard === TokenStandard.Native ? NATIVE_CHAIN_ID : suggestion.address;
    if (isToken && address) {
      const chainId = fromGraphQLChain(suggestion.chain);
      if (chainId) {
        const searchResult = searchTokenToTokenSearchResult({ ...suggestion, address, chainId });
        dispatch(addToSearchHistory({ searchResult }));
      }
    }
    toggleOpen();
    sendAnalyticsEvent(InterfaceEventName.NAVBAR_RESULT_SELECTED, { ...eventProperties });
  }, [suggestion, isToken, toggleOpen, eventProperties, dispatch]);
  const path = isToken ? getTokenDetailsURL({ address: suggestion.address, chainUrlParam: suggestion.chain.toLowerCase() }) : `/nfts/collection/${suggestion.address}`;
  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === "Enter" && isHovered) {
        event.preventDefault();
        navigate(path);
        handleClick();
      }
    };
    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [toggleOpen, isHovered, suggestion, navigate, handleClick, path]);
  const shortenedAddress = useMemo(() => {
    if (isToken && suggestion.address && suggestion.address !== NATIVE_CHAIN_ID) {
      return shortenAddress(suggestion.address);
    }
    return null;
  }, [suggestion, isToken]);
  return <StyledLink
    to={path}
    onClick={handleClick}
    $isFocused={isHovered}
    onMouseEnter={() => !isHovered && setHoveredIndex(index)}
    onMouseLeave={() => isHovered && setHoveredIndex(void 0)}
    data-testid={isToken ? `searchbar-token-row-${suggestion.chain}-${suggestion.address ?? NATIVE_CHAIN_ID}` : ""}
  >
      <Flex row gap="$spacing8" shrink grow overflow="hidden">
        {isToken ? <QueryTokenLogo
    token={suggestion}
    symbol={suggestion.symbol}
    size={36}
    primaryImg={suggestion.project?.logoUrl}
  /> : brokenCollectionImage ? <BrokenCollectionImage /> : <CollectionImage
    src={suggestion.imageUrl}
    alt={suggestion.name}
    onError={() => setBrokenCollectionImage(true)}
  />}
        <Flex alignItems="flex-start" justifyContent="flex-start" shrink grow>
          <Flex row gap="$spacing4" shrink width="95%" {...isToken && { alignItems: "center" }}>
            <PrimaryText lineHeight="24px">{suggestion.name}</PrimaryText>
            {isToken ? warningIconColor && <WarningIcon
    severity={tokenWarningSeverity}
    size="$icon.16"
    flexShrink={0}
    flexGrow={0}
    strokeColorOverride={warningIconColor}
  /> : suggestion.isVerified && <GoldenBadgeVerified size={"$icon.16"} />}
          </Flex>
          <Flex row gap="$spacing4">
            <ThemedText.SubHeaderSmall lineHeight="20px">
              {isToken ? suggestion.symbol : t("search.results.count", {
    count: suggestion?.stats?.total_supply ?? 0
  })}
            </ThemedText.SubHeaderSmall>
            {shortenedAddress && <ThemedText.SubHeaderSmall lineHeight="20px" color="neutral3">
                {shortenedAddress}
              </ThemedText.SubHeaderSmall>}
          </Flex>
        </Flex>
      </Flex>

      <SecondaryContainer>
        <Flex row gap="$spacing4">
          <PrimaryText width="100%">
            {isToken ? formatFiatPrice({ price: asSearchTokenSuggestion(suggestion).market?.price?.value ?? void 0 }) : `${formatNumberOrString({ input: suggestion.stats?.floor_price, type: NumberType.NFTToken })} ETH`}
          </PrimaryText>
        </Flex>

        <PriceChangeContainer>
          {isToken ? <>
              <DeltaArrow
    delta={asSearchTokenSuggestion(suggestion).market?.pricePercentChange?.value ?? void 0}
    formattedDelta={formatDelta(asSearchTokenSuggestion(suggestion).market?.pricePercentChange?.value ?? void 0)}
  />
              <ThemedText.BodySmall>
                <DeltaText delta={asSearchTokenSuggestion(suggestion).market?.pricePercentChange?.value ?? void 0}>
                  {formatDelta(Math.abs(asSearchTokenSuggestion(suggestion).market?.pricePercentChange?.value ?? 0))}
                </DeltaText>
              </ThemedText.BodySmall>
            </> : <ThemedText.BodySmall color="neutral2">
              <Trans i18nKey="common.floor" />
            </ThemedText.BodySmall>}
        </PriceChangeContainer>
      </SecondaryContainer>
    </StyledLink>;
}
const SkeletonContent = styled(Column)`
  width: 100%;
`;
export function SkeletonRow() {
  return <SkeletonSuggestionRow $isFocused={false}>
      <Flex row width="100%" gap="$gap4" alignItems="center">
        <BrokenCollectionImage />
        <SkeletonContent gap="sm">
          <Flex row justifyContent="space-between">
            <LoadingBubble height="20px" width="180px" containerWidth="180px" />
            <LoadingBubble height="20px" width="48px" containerWidth="48px" />
          </Flex>

          <Flex row justifyContent="space-between">
            <LoadingBubble height="16px" width="120px" containerWidth="120px" />
            <LoadingBubble height="16px" width="48px" containerWidth="48px" />
          </Flex>
        </SkeletonContent>
      </Flex>
    </SkeletonSuggestionRow>;
}
